/**
 * Protocol Form Component
 */

import { useState, useEffect } from 'react';
import type { Protocol, ProtocolFormData, ProtocolStatus } from '../../types';
import { PROTOCOL_PHASES, THERAPEUTIC_AREAS } from '../../types';

interface ProtocolFormProps {
  protocol?: Protocol | null;
  onSubmit: (data: ProtocolFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  /**
   * eReg P4 (§5.6): registry-picker prefill on the create flow. Applied
   * whenever the object identity changes (memoize in the caller) — every key
   * present in the object OVERWRITES the form field (including with ''), so a
   * pick is a deterministic prefill, never a merge with stale typing.
   */
  initialValues?: Partial<ProtocolFormData> | null;
}

export function ProtocolForm({ protocol, onSubmit, onCancel, isLoading = false, initialValues = null }: ProtocolFormProps) {
  const [formData, setFormData] = useState<ProtocolFormData>({
    protocol_number: '',
    protocol_title: '',
    sponsor_name: '',
    therapeutic_area: '',
    phase: '',
    status: 'setup',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when protocol prop changes
  useEffect(() => {
    if (protocol) {
      setFormData({
        protocol_number: protocol.protocol_number || '',
        protocol_title: protocol.protocol_title || '',
        sponsor_name: protocol.sponsor_name || '',
        therapeutic_area: protocol.therapeutic_area || '',
        phase: protocol.phase || '',
        status: protocol.status,
      });
    }
  }, [protocol]);

  // Apply registry-picker prefill (create flow only — protocol wins when editing)
  useEffect(() => {
    if (!initialValues || protocol) return;
    setFormData(prev => {
      const merged = { ...prev };
      for (const [key, value] of Object.entries(initialValues)) {
        if (value !== undefined) {
          (merged as any)[key] = value;
        }
      }
      return merged;
    });
    setErrors({});
  }, [initialValues, protocol]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.protocol_number.trim()) {
      newErrors.protocol_number = 'Protocol number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to save protocol' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300">
          {errors.submit}
        </div>
      )}

      {/* Protocol Number */}
      <div>
        <label htmlFor="protocol_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Protocol Number <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <input
          type="text"
          id="protocol_number"
          name="protocol_number"
          value={formData.protocol_number}
          onChange={handleChange}
          placeholder="e.g., ABC-123-001"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.protocol_number ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {errors.protocol_number && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.protocol_number}</p>
        )}
      </div>

      {/* Protocol Title */}
      <div>
        <label htmlFor="protocol_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Protocol Title
        </label>
        <input
          type="text"
          id="protocol_title"
          name="protocol_title"
          value={formData.protocol_title}
          onChange={handleChange}
          placeholder="e.g., A Phase 3 Study of Drug X in Patients with..."
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Sponsor Name */}
      <div>
        <label htmlFor="sponsor_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Sponsor Name
        </label>
        <input
          type="text"
          id="sponsor_name"
          name="sponsor_name"
          value={formData.sponsor_name}
          onChange={handleChange}
          placeholder="e.g., Pharma Company Inc."
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Phase and Therapeutic Area */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phase" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phase
          </label>
          <select
            id="phase"
            name="phase"
            value={formData.phase}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select phase...</option>
            {PROTOCOL_PHASES.map(phase => (
              <option key={phase} value={phase}>
                {phase}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="therapeutic_area" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Therapeutic Area
          </label>
          <select
            id="therapeutic_area"
            name="therapeutic_area"
            value={formData.therapeutic_area}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select therapeutic area...</option>
            {THERAPEUTIC_AREAS.map(area => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status (only show if editing) */}
      {protocol && (
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="setup">Setup</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="closed">Closed</option>
          </select>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {formData.status === 'setup' && 'Protocol is being configured'}
            {formData.status === 'active' && 'Protocol is ready for reviews'}
            {formData.status === 'paused' && 'Protocol is temporarily paused'}
            {formData.status === 'closed' && 'Protocol is no longer accepting reviews'}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2 min-h-[44px] sm:min-h-0"
        >
          {isLoading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {protocol ? 'Save Changes' : 'Create Protocol'}
        </button>
      </div>
    </form>
  );
}
